import { useRequest } from 'features/request/useRequest';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';
import { getWhere } from 'pages/Project/Common/AddWorkOrderPriceForm/TabContent/PriceTable/hooks/getWhere';

import { debounce } from 'debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { IPriceTabDataProps } from '../helpersAndTypes';

import { uerWorkOrderUerSelectionList } from './addPricesToWorkOrderQueries';

export const usePricesTableRows = (props: IPriceTabDataProps) => {
    const { workOrderId, constructionTypeId, workOrderObjectIds, chapter } = props;
    const [queryWhere, setQueryWhere] = useState<Record<string, any>>({});

    const [searchString, setSearchString] = useState('');

    const onSearch = useCallback(({ value }) => {
        setQueryWhere((currentFilters) => ({ ...currentFilters, searchString: value }));
    }, []);

    const changeFilterHandler = useCallback(({ fieldName, checkedValues }) => {
        setQueryWhere((prevValue) => ({
            ...prevValue,
            [fieldName]: checkedValues,
        }));
    }, []);

    const debouncedSearch = useMemo(() => debounce(onSearch, 500), [onSearch]);

    const onSearchStringChange = useCallback(
        ({ value }) => {
            setSearchString(value);
            debouncedSearch({ value });
        },
        [debouncedSearch],
    );

    const onSearchStringClear = useCallback(() => {
        if (searchString) {
            setSearchString('');

            setQueryWhere((prevFilters) => ({
                ...prevFilters,
                searchString: undefined,
            }));
        }
    }, [searchString]);

    const where = useMemo(() => getWhere(chapter, queryWhere), [chapter, queryWhere]);

    const variables = useMemo(() => {
        if (chapter && constructionTypeId && workOrderId && workOrderObjectIds?.length) {
            return {
                workOrderId,
                constructionTypeId,
                workOrderObjectIds,
                chapter,
                where,
            };
        }

        return undefined;
    }, [chapter, constructionTypeId, where, workOrderId, workOrderObjectIds]);

    const [data, , , request] = useRequest({
        requestId: 'uerWorkOrderUerSelectionList',
        query: uerWorkOrderUerSelectionList,
        withEffect: false,
        variables,
    });

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId: 'uerWorkOrderUerSelectionList',
        requestFn: request,
        requestVariables: variables,
    });

    useEffect(() => {
        if (data?.uerWorkOrderUersSelectionListAsync?.items && getRowParams?.current) {
            const items = data?.uerWorkOrderUersSelectionListAsync?.items;

            getRowParams?.current?.successCallback(items, data?.uerWorkOrderUersSelectionListAsync?.totalCount);
        }
    }, [
        data?.uerWorkOrderUersSelectionListAsync?.items,
        data?.uerWorkOrderUersSelectionListAsync?.totalCount,
        getRowParams,
    ]);

    return {
        dataSource,
        onSearchStringClear,
        onSearchStringChange,
        searchString,
        onSearch,
        filters: queryWhere,
        changeFilterHandler,
    };
};
